import {
  IconDatabase,
  IconQuote,
  IconSearch,
  IconLock,
  IconUsers,
  IconPlug,
  IconCloud,
  IconFile,
} from "@tabler/icons-react";
import { cn } from "../../lib/utils";

export function FeaturesSectionWithHoverEffects() {
  const features = [
    {
      title: "Built for Data-Heavy Litigation",
      description:
        "AnyLawyer's Deep Context Analysis supports hundreds of thousands of documents per project, enabling full-corpus discovery.",
      icon: <IconDatabase className="h-8 w-8" />,
    },
    {
      title: "Citations & Verification",
      description:
        "All answers are supported by pinpoint citations from the original document. Trust but verify.",
      icon: <IconQuote className="h-8 w-8" />,
    },
    {
      title: "Private Tenant Deployment",
      description:
        "Optional deployment to your own private cloud infrastructure. We support AWS, GCP and Azure.",
      icon: <IconCloud className="h-8 w-8" />,
    },
    {
      title: "Multi-Step Legal Reasoning",
      description:
        "Our AI breaks down complex legal queries into logical steps to find answers buried across hundreds of files.",
      icon: <IconSearch className="h-8 w-8" />,
    },
    {
      title: "Ecosystem Integration",
      description:
        "AnyLawyer connects to your DMS. Google Drive integration available; SharePoint and OneDrive coming soon.",
      icon: <IconPlug className="h-8 w-8" />,
      soon: false,
    },
    {
      title: "Strict Data Isolation",
      description:
        "Project-level isolation ensures client data never crosses. Encrypted at rest and in transit. EU-hosted.",
      icon: <IconLock className="h-8 w-8" />,
    },
    {
      title: "Export to DOCX",
      description:
        "Export your answers to DOCX format for easy sharing and collaboration.",
      icon: <IconFile className="h-8 w-8" />,
    },
    {
      title: "Team Collaboration",
      description:
        "Share documents, insights, prompts, and search results with your team. Create collaborative workspaces for case research and knowledge sharing.",
      icon: <IconUsers className="h-8 w-8" />,
    },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4  relative z-10 py-10 max-w-7xl mx-auto">
      {features.map((feature, index) => (
        <Feature key={feature.title} {...feature} index={index} />
      ))}
    </div>
  );
}

const Feature = ({
  title,
  description,
  icon,
  soon,
  index,
}: {
  title: string;
  soon?: boolean;
  description: string;
  icon: React.ReactNode;
  index: number;
}) => {
  return (
    <div
      className={cn(
        "flex flex-col lg:border-r py-10 relative group/feature border-slate-100",
        (index === 0 || index === 4) && "lg:border-l border-slate-100",
        index < 4 && "lg:border-b border-slate-100"
      )}
    >
      {index < 4 && (
        <div className="opacity-0 group-hover/feature:opacity-100 transition duration-200 absolute inset-0 h-full w-full bg-gradient-to-t from-slate-50/50 to-transparent pointer-events-none" />
      )}
      {index >= 4 && (
        <div className="opacity-0 group-hover/feature:opacity-100 transition duration-200 absolute inset-0 h-full w-full bg-gradient-to-b from-slate-50/50 to-transparent pointer-events-none" />
      )}
      <div className="mb-6 relative z-10 px-10 text-primary/80 group-hover/feature:text-primary transition-colors duration-200">
        {icon}
      </div>
      <div className="text-lg font-bold mb-2 relative z-10 px-10">
        <div className="absolute left-0 inset-y-0 h-6 group-hover/feature:h-8 w-1 rounded-tr-full rounded-br-full bg-slate-200 group-hover/feature:bg-primary transition-all duration-200 origin-center" />
        <span className="group-hover/feature:translate-x-2 transition duration-200 inline-block text-slate-900">
          {title}
          {soon && (
            <div className="text-[10px] font-medium bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full inline-block ml-2 align-middle border border-blue-100">
              Coming Soon
            </div>
          )}
        </span>
      </div>
      <p className="text-sm text-slate-500 max-w-xs relative z-10 px-10 leading-relaxed group-hover/feature:text-slate-600 transition-colors duration-200">
        {description}
      </p>
    </div>
  );
};
